import React, { useState } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { useEditor } from '@grapesjs/react';
import type { Component, Editor } from 'grapesjs';
import LayerItem from './LayerItem';
import { ScrollView } from '@constants/Themed';

type DragRect = {
  y: number;
  h: number;
  pointerInside: boolean;
};

const LAYER_PAD = 5;

type CanMoveResult = ReturnType<Editor['Components']['canMove']>;

interface CanMove extends Partial<Omit<CanMoveResult, 'source'>> {
  canMoveInside?: CanMoveResult;
  source?: Component | null;
  index?: number;
}

export default function CustomLayerManager({ root }: { root: Component }) {
  const editor = useEditor();
  const [pointerDown, setPointerDown] = useState(false);
  const [canMoveRes, setCanMoveRes] = useState<CanMove>({});
  const [cmpPointerOver, setCmpPointerOver] = useState<Component>();
  const [dragging, setDragging] = useState<Component>();
  const [dragParent, setDragParent] = useState<Component>();
  const [dragRect, setDragRect] = useState<DragRect>();

  const onDragStart = () => {
    setPointerDown(true);
  };

  const onDragMove = (ev: any) => {
    if (!pointerDown) return;
    // Get the element and component being dragged
    const layerRect = ev.nativeEvent.layout;
    const isBefore = ev.nativeEvent.pageY < layerRect.y + layerRect.height / 2;
    const cmpSource = !dragging ? cmpPointerOver : dragging;
    const cmpTarget = cmpSource?.parent();
    const cmpIndex = cmpSource?.index() + (isBefore ? 0 : 1);

    setDragging(cmpSource);
    setCmpPointerOver(cmpSource);
    const canMove = editor.Components.canMove(cmpTarget!, cmpSource, cmpIndex);
    const canMoveInside = editor.Components.canMove(cmpSource!, cmpSource);
    const canMoveRes: CanMove = {
      ...canMove,
      canMoveInside,
      index: cmpIndex,
    };

    let pointerInside = false;
    if (
      canMoveInside.result &&
      ev.nativeEvent.pageY > layerRect.y + LAYER_PAD &&
      ev.nativeEvent.pageY < layerRect.y + layerRect.height - LAYER_PAD
    ) {
      pointerInside = true;
      canMoveRes.target = cmpSource;
      delete canMoveRes.index;
    }

    setDragParent(pointerInside ? cmpSource : undefined);
    setCanMoveRes(canMoveRes);
    setDragRect({
      pointerInside,
      y: layerRect.y + (isBefore ? 0 : layerRect.height),
      h: layerRect.height,
    });
  };

  const onDragEnd = () => {
    if (canMoveRes?.result) {
      canMoveRes.source?.move(canMoveRes.target!, { at: canMoveRes.index });
    }
    setCanMoveRes({});
    setPointerDown(false);
    setDragging(undefined);
    setCmpPointerOver(undefined);
    setDragParent(undefined);
    setDragRect(undefined);
  };

  const dragLevel = (cmpPointerOver ? cmpPointerOver.parents() : []).length;
  const showIndicator =
    !!dragging && !!dragRect && !!canMoveRes.result && !dragRect.pointerInside;
  const indicatorStyle = showIndicator
    ? { top: dragRect.y, left: 0, marginLeft: dragLevel * 10 + 20 }
    : {};

  return (
    <ScrollView
      style={[styles.container, {maxHeight: "100%"}]}
      onStartShouldSetResponder={() => true}
      onResponderMove={onDragMove}
      onResponderRelease={onDragEnd}
    >
      {!!root && (
        <LayerItem
          component={root}
          level={-1}
          draggingCmp={dragging}
          dragParent={dragParent}
        />
      )}
      {showIndicator && (
        <View
          style={[styles.indicator, indicatorStyle]}
        ></View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    margin: -10,
  },
  indicator: {
    position: 'absolute',
    backgroundColor: 'yellow',
    height: 2,
    width: '100%',
  },
});
